import { useCallback, useEffect, useRef, useState } from 'react';
import type { FieldValues, UseFormReturn } from 'react-hook-form';
import { useUserStore } from '@/store/userStore';
import { buildDraftKey, useFormDraftStore } from '@/store/formDraftStore';

/**
 * Brouillon persisté pour un formulaire react-hook-form — pendant RHF de
 * useStateDraft. Sauvegarde debouncée de `form.getValues()` (moins `exclude`),
 * flush au passage en background, restauration au mount OU à la demande via
 * `mergeDraftOver`.
 *
 * `mergeDraftOver(values)` est LA réponse aux dialogs qui possèdent leur
 * propre effet `form.reset(valeursServeur)` (écrasant toute restauration
 * faite au mount) : remplacer ce reset par
 * `form.reset(mergeDraftOver(valeursServeur))`. Il lit le store EN DIRECT au
 * moment de l'appel — un effet reset qui re-tire (deps `open`, identité qui
 * churn) ré-applique donc toujours le brouillon le plus frais. Sans brouillon,
 * il rend `values` inchangées (no-op).
 *
 * Garde anti-brouillon-fantôme : la sauvegarde ne part que si
 * `form.formState.isDirty` (lu impérativement au moment du save). Un
 * `form.reset(...)` pose isDirty=false et les `setValue` programmatiques (sans
 * shouldDirty) ne le lèvent pas → seule la frappe réelle de l'utilisateur
 * produit un brouillon. Ouvrir puis refermer un dialog d'édition sans taper ne
 * laisse AUCUNE trace.
 */
export function useFormDraft<TValues extends FieldValues>(
  formId: string,
  form: UseFormReturn<TValues>,
  options?: {
    /**
     * Identifiant d'entité pour les formulaires d'édition : la clé devient
     * `${formId}:${entityId ?? 'new'}`. Omis → clé = formId seul.
     */
    entityId?: string | number | null;
    /** Clés retirées du brouillon au save ET au restore. */
    exclude?: string[];
    isEmpty?: (values: Partial<TValues>) => boolean;
    enabled?: boolean;
    /**
     * false pour les dialogs qui reseedent eux-mêmes via un effet reset —
     * la restauration passe alors par mergeDraftOver dans cet effet.
     */
    restoreOnMount?: boolean;
    /** Re-hydratation du brouillon brut (dates ISO|Date → Date, etc.). */
    revive?: (data: Record<string, unknown>) => Partial<TValues>;
  }
) {
  const {
    entityId,
    exclude,
    isEmpty,
    enabled = true,
    restoreOnMount = true,
    revive,
  } = options ?? {};
  const gymId = useUserStore((s) => s.gymId);
  const userId = useUserStore((s) => s.user?.id ?? null);
  const stem = entityId !== undefined ? `${formId}:${entityId ?? 'new'}` : formId;
  const key = buildDraftKey(stem, gymId, userId);

  // Abonnement RENDER-TIME à isDirty : formState est un Proxy à abonnement —
  // sans cette lecture pendant le rendu, RHF ne maintient pas isDirty et les
  // lectures impératives dans les callbacks (save/flush) restent à false,
  // désarmant silencieusement la garde anti-fantôme.
  const { isDirty } = form.formState;
  void isDirty;

  const [hasRestoredDraft, setHasRestoredDraft] = useState(false);
  const hasRestoredDraftRef = useRef(false);
  const restoreAttemptedRef = useRef(false);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestRef = useRef({ form, key, exclude, isEmpty, enabled, revive });
  latestRef.current = { form, key, exclude, isEmpty, enabled, revive };

  const stripExcluded = useCallback((data: Record<string, unknown>) => {
    const ex = latestRef.current.exclude;
    if (!ex || ex.length === 0) return data;
    const next = { ...data };
    for (const k of ex) delete next[k];
    return next;
  }, []);

  /** Brouillon courant, sans exclus, revivé — ou null. */
  const readDraft = useCallback((): Partial<TValues> | null => {
    const draft = useFormDraftStore.getState().getDraft(latestRef.current.key);
    const raw = draft?.data as Record<string, unknown> | undefined;
    if (!raw || typeof raw !== 'object') return null;
    const stripped = stripExcluded(raw);
    const rev = latestRef.current.revive;
    const data = (rev ? rev(stripped) : stripped) as Partial<TValues>;
    const empty = latestRef.current.isEmpty;
    if (empty && empty(data)) return null;
    return data;
  }, [stripExcluded]);

  const markRestored = useCallback(() => {
    hasRestoredDraftRef.current = true;
    setHasRestoredDraft(true);
  }, []);

  /**
   * Superpose le brouillon courant à `values` (pour les effets reset des
   * dialogs). Appeler depuis un effet ou un handler, jamais pendant le rendu.
   */
  const mergeDraftOver = useCallback(
    (values: TValues): TValues => {
      const data = readDraft();
      if (!data) return values;
      markRestored();
      return { ...values, ...data };
    },
    [readDraft, markRestored]
  );

  // Restauration au mount (formulaires sans effet reset concurrent).
  useEffect(() => {
    if (!enabled || restoreAttemptedRef.current) return;
    restoreAttemptedRef.current = true;
    if (!restoreOnMount) return;
    const data = readDraft();
    if (!data) return;
    const { form: f } = latestRef.current;
    // keepDefaultValues : les valeurs restaurées comptent comme « dirty »,
    // donc le brouillon continue de se rafraîchir à la frappe suivante.
    f.reset({ ...f.getValues(), ...data }, { keepDefaultValues: true });
    markRestored();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  const scheduleSave = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      const { form: f, key: k, isEmpty: empty, enabled: on } = latestRef.current;
      if (!on) return;
      // Garde anti-fantôme : rien n'est écrit tant que l'utilisateur n'a pas
      // réellement modifié le formulaire.
      if (!f.formState.isDirty) return;
      const values = stripExcluded(f.getValues() as Record<string, unknown>);
      if (empty && empty(values as Partial<TValues>)) return;
      useFormDraftStore.getState().saveDraft(k, values);
    }, 400);
  }, [stripExcluded]);

  // Sauvegarde debouncée sur toute frappe.
  useEffect(() => {
    if (!enabled) return;
    const sub = form.watch(() => scheduleSave());
    return () => {
      sub.unsubscribe();
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [form, enabled, scheduleSave]);

  // Flush immédiat quand l'app passe en background.
  useEffect(() => {
    if (!enabled) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'hidden') return;
      const { form: f, key: k, isEmpty: empty } = latestRef.current;
      if (!f.formState.isDirty) return;
      const values = stripExcluded(f.getValues() as Record<string, unknown>);
      if (empty && empty(values as Partial<TValues>)) return;
      useFormDraftStore.getState().saveDraft(k, values);
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => document.removeEventListener('visibilitychange', onVisibility);
  }, [enabled, stripExcluded]);

  const clearDraft = useCallback(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    useFormDraftStore.getState().clearDraft(latestRef.current.key);
  }, []);

  return { hasRestoredDraft, hasRestoredDraftRef, clearDraft, mergeDraftOver };
}
